import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SendHorizonal } from 'lucide-react';
import { JobMessage, MESSAGE_AUTHOR_LABELS, apiErrorMessage, formatRelativeTime } from '../services/servicesShared';

/**
 * The steering channel (job_messages): what the customer and you have told
 * the job, and its replies. A message from here is steward steering; it
 * wakes the job with reason=message when the job is on.
 */
export const JobSteering: React.FC<{
    jobId: string;
    messages: JobMessage[];
    jobOn: boolean;
    onSent: (message: JobMessage) => void;
}> = ({ jobId, messages, jobOn, onSent }) => {
    const { post } = useApi();
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const endRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'nearest' });
    }, [messages.length]);

    const send = async () => {
        const body = text.trim();
        if (!body) return;
        setSending(true);
        try {
            const response = await post(`/jobs/${jobId}/steer`, { text: body, kind: 'steer' });
            onSent(response.data);
            setText('');
            if (!jobOn) toast.info('Saved. The job is off, so it will read this when turned back on.');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not send the message'));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-lg border">
            <div className="px-3 py-2 border-b">
                <h3 className="text-sm font-semibold">Steering</h3>
                <p className="text-xs text-muted-foreground">
                    Direct the job in words. Durable steering becomes memory with precedence over everything but guardrails.
                </p>
            </div>
            <div className="max-h-80 overflow-y-auto scrollbar-thin px-3 py-2 space-y-3">
                {messages.length ? (
                    messages.map((message) => {
                        const mine = message.author === 'steward';
                        const fromJob = message.author === 'job';
                        return (
                            <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                                <div
                                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                                        mine ? 'bg-primary text-primary-foreground' : fromJob ? 'bg-muted' : 'border'
                                    }`}
                                >
                                    <div className={`text-[11px] mb-0.5 ${mine ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                        {MESSAGE_AUTHOR_LABELS[message.author] || message.author}
                                        {message.kind && message.kind !== 'steer' && message.kind !== 'reply' ? ` · ${message.kind}` : ''}
                                        {' · '}
                                        {formatRelativeTime(message.created_at)}
                                        {!fromJob && !message.handled_at ? ' · unread by the job' : ''}
                                    </div>
                                    {message.text}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className="text-sm text-muted-foreground">No messages yet.</p>
                )}
                <div ref={endRef} />
            </div>
            <div className="border-t p-2 flex items-end gap-2">
                <Textarea
                    rows={2}
                    className="min-h-0"
                    placeholder="Tell the job what to do differently…"
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                            event.preventDefault();
                            void send();
                        }
                    }}
                />
                <Button size="icon" onClick={send} disabled={sending || !text.trim()} title="Send (⌘↵)">
                    <SendHorizonal className="h-4 w-4" />
                </Button>
            </div>
        </div>
    );
};

export default JobSteering;
